import { createAsyncThunk, createSlice } from '@reduxjs/toolkit';
import { addWell, addWells, deleteWell, deleteWells } from './wellSlice';
import { addPetroNinjaListApi, deleteListApi, getListsApi } from '../api/listAPI';

const initialState = {
  byId: {},
  allIds: [],
  currentListId: "",
};

export const getLists = createAsyncThunk(
  'lists/getLists',
  async (payload) => {
    try {
      const lists = await getListsApi(payload.listIds);

      return { projectId: payload.projectId, lists: lists };
    } catch (error) {
      return;
    };
  }
);

export const addList = createAsyncThunk(
  'lists/addList',
  async (payload) => {
    try {
      return payload.list;
    } catch (error) {
      return;
    };
  }
);

export const addPetroNinjaList = createAsyncThunk(
  'lists/addPetroNinjaList',
  async (payload) => {
    try {
      const list = await addPetroNinjaListApi(payload.user, payload.listId);
      const projectId = payload.projectId;
      return { projectId: projectId, list: list };
    } catch (error) {
      return;
    };
  }
);

export const deleteList = createAsyncThunk(
  'lists/deleteList',
  async (payload) => {
    try {
      await deleteListApi(payload.listId);
      return {projectId: payload.projectId, listId: payload.listId};
    } catch (error) {
      return;
    };
  }
);

export const listSlice = createSlice({
  name: 'lists',
  initialState,
  reducers: {
    addLists: (state, action) => {
      const lists = action.payload.lists;
      lists.allIds?.forEach(listId => {
        state.byId[listId] = lists.byId[listId];
        if (state.allIds.indexOf(listId) === -1) state.allIds.push(listId);
      });
    },
    updateList: (state, action) => {
      const listId = action?.payload?.list?.id;
      const list = action?.payload?.list;
      state.byId[listId] = list;
    },
    setLists: (state, action) => {
      const lists = action.payload.lists;
      const allIds = lists.allIds;
      allIds.forEach(id => {
        state.byId[id] = lists.byId[id];
        if (state.allIds.indexOf(id) === -1) state.allIds.push(id);
      });
      state.currentListId = lists.currentListId;
    },
    makeWellCurrent: (state, action) => {
      const {listId, wellId} = action.payload;
      state.byId[listId].currentWellId = wellId;
    },
    makeListCurrent: (state, action) => {
      const listId = action.payload;
      if (state.currentListId !== listId) {
        state.currentListId = listId;
      };
    },
    makeTypeCurveCurrent: (state, action) => {
      const { listId, typeCurveId } = action.payload;
      state.byId[listId].typeCurveId = typeCurveId;
    },
    makeGroupWellCurrent: (state, action) => {
      const { listId, groupWellId } = action.payload;
      state.byId[listId].groupWellId = groupWellId;
    },
    setForecastListTab: (state, action) => {
      const { listId, forecastListTab } = action.payload;
      state.byId[listId].forecastListTab = forecastListTab;
    },
    setListFilters: (state, action) => {
      const { listId, filters } = action.payload;
      state.byId[listId].filters = filters;
    }
  },
  extraReducers: (builder) => {
    builder.addCase(getLists.fulfilled, (state, action) => {
      const lists = action.payload.lists;
      if (lists?.length > 0) {
        lists.forEach(list => {
          state.byId[list.id] = list;
          state.byId[list.id].filters = {};
          if (state.allIds.indexOf(list.id) === -1) state.allIds.push(list.id);
        });
        state.currentListId = lists[lists.length-1]?.id;
      };
    })
    builder.addCase(addList.fulfilled, (state, action) => {
      const list = action.payload;
      state.byId[list.id] = list;
      if (state.allIds.indexOf(list.id) === -1) state.allIds.push(list.id);
      state.currentListId = list.id;
    })
    // builder.addCase(addPetroNinjaList.fulfilled, (state, action) => {
    //   const list = action.payload?.list;
    //   if (list) {
    //     state.byId[list.id] = list;
    //     if (state.allIds.indexOf(list.id) === -1) state.allIds.push(list.id);
    //     state.currentListId = list.id;
    //   };
    // })
    builder.addCase(deleteList.fulfilled, (state, action) => {
      const listId = action.payload.listId;
      delete state.byId[listId];
      state.allIds = state.allIds.filter(id => id !== listId);
      state.currentListId = state.allIds[0] || "";
    })
    builder.addCase(addWell, (state, action) => {
      try {
        const { listId, well } = action.payload;
        if (!state.byId[listId].wellUwis.includes(well.uwi)) {
          state.byId[listId].wellUwis.push(well.uwi);
        };
      } catch (error) {
        return;
      };
    })
    builder.addCase(addWells, (state, action) => {
      const uwis = [...action.payload.uwis];
      const listId = action.payload.listId;
      uwis?.forEach(uwi => {
        if (state.byId[listId]?.wellUwis?.indexOf(uwi) === -1) {
          state.byId[listId]?.wellUwis?.push(uwi);
        };
      });
    })
    builder.addCase(deleteWell, (state, action) => {
      const { listId, wellUwi } = action.payload;
      state.byId[listId].wellUwis = state.byId[listId].wellUwis.filter(uwi => uwi !== wellUwi);
    })
    builder.addCase(deleteWells, (state, action) => {
      const { listId, uwis } = action.payload;
      state.byId[listId].wellUwis = state.byId[listId].wellUwis.filter(uwi => uwis.indexOf(uwi) === -1);
    })
  }
});

export const {
  addLists,
  updateList,
  setLists,
  makeWellCurrent, 
  makeListCurrent, 
  toggleCheckWell, 
  toggleWellVisibility, 
  makeTypeCurveCurrent,
  makeGroupWellCurrent,
  setForecastListTab,
  setListFilters
} = listSlice.actions;

export const selectLists = (state) => state.lists;

export default listSlice.reducer;